import { Dashboard } from '@fullstory/proto/fs/services/dx-objects';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { isNonNull } from '@fullstory/typeutils';
import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { AppState } from '../../types';
import { getObjectWithOverlayStorageId } from '../../utils';

const RECENT_DASHBOARDS_MAX_LENGTH = 5;

export const dashboardsById = (state: AppState) => state.dashboards.dashboardsById;
export const dashboardsLoading = (state: AppState) => state.dashboards.dashboards.loading;
export const initialDashboardsIds = (state: AppState) => state.dashboards.initialDashboardsIds;
export const initialDashboardsLoading = (state: AppState) =>
  state.dashboards.initialDashboards.loading;
export const cardsById = (state: AppState) => state.dashboards.cardsById;
export const dashboardsAccess = (state: AppState) => state.dashboards.access;
export const getDashboardCardLimit = (state: AppState) => state.dashboards.cardLimit;

export const makeGetCardsByDashboard = (dashboardId: int64, overlay?: string) =>
  createSelector(dashboardsById, cardsById, (dashboardsById, cardsById) =>
    dashboardsById[dashboardId as string]?.cards?.map(
      c => cardsById[getObjectWithOverlayStorageId(c as string, overlay)],
    ),
  );

// Alphabetical list of dashboards
export const dashboardList = createSelector(dashboardsById, dashboards => {
  const list: Dashboard[] = [];
  for (const key in dashboards) {
    const dashboard = dashboards[key];
    if (dashboard) list.push(dashboard);
  }
  return list.sort(nameAndIdCompare);
});

export const getRecentDashboardsIds = createSelector(initialDashboardsIds, initialDashboardsIds =>
  initialDashboardsIds ? initialDashboardsIds.recent : [],
);

const getStarredDashboardsIds = createSelector(initialDashboardsIds, initialDashboardsIds =>
  initialDashboardsIds ? initialDashboardsIds.starred : [],
);

export const getStarredDashboards = createSelector(
  initialDashboardsIds,
  dashboardsById,
  (initialDashboardsIds, dashboards) => {
    if (isEmpty(dashboards) || isEmpty(initialDashboardsIds.starred)) return [];
    const starredDashboards = initialDashboardsIds.starred
      .map(d => dashboards[d as string])
      .filter(isNonNull);
    return starredDashboards.sort(nameAndIdCompare);
  },
);

/** Returns a list of all dashboards along with whether each dashboard is starred */
export const getDashboardLibrary = createSelector(
  dashboardList,
  getStarredDashboardsIds,
  (dashboards, starredIds) => {
    const starred = new Set(starredIds);
    return dashboards.map(d => ({ ...d, starred: d.starred || starred.has(d.id) }));
  },
);

export const getRecentDashboards = createSelector(
  initialDashboardsIds,
  dashboardsById,
  (initialDashboardsIds, dashboards): Dashboard[] => {
    if (isEmpty(dashboards) || isEmpty(initialDashboardsIds.recent)) return [];

    // If there are starred dashboards, filter recent dashboards to not include starred dashboards
    const recents = initialDashboardsIds.starred
      ? initialDashboardsIds.recent.filter(
          recentId => !initialDashboardsIds.starred.find(starredId => starredId == recentId),
        )
      : initialDashboardsIds.recent;
    const recentDashboards = recents.map(d => dashboards[d as string]);
    return recentDashboards
      .filter(isNonNull)
      .sort((a, b) => {
        const aDate = Timestamp.mustDate(a.lastViewed).getTime();
        const bDate = Timestamp.mustDate(b.lastViewed).getTime();
        return bDate - aDate;
      })
      .slice(0, RECENT_DASHBOARDS_MAX_LENGTH);
  },
);

/** This returns all of the recent dashboards regardless of starred state */
export const getAllRecentDashboards = createSelector(
  initialDashboardsIds,
  dashboardsById,
  (initialDashboardsIds, dashboards): Dashboard[] => {
    if (isEmpty(dashboards) || isEmpty(initialDashboardsIds.recent)) return [];

    const recentDashboards = initialDashboardsIds.recent
      .map(d => dashboards[d as string])
      .filter(isNonNull);
    return recentDashboards;
  },
);

/** This returns all of the non-recent and non-starred dashboards */
export const getOtherDashboards = createSelector(
  dashboardList,
  getRecentDashboards,
  getStarredDashboards,
  (dashboards, recents, starred): Dashboard[] => {
    if (isEmpty(dashboards)) return [];

    // filter all dashboards to not include starred or recent dashboards
    const otherDashboards = dashboards.filter(d => !recents.includes(d) && !starred.includes(d));
    return otherDashboards.sort(nameAndIdCompare);
  },
);

export const nameAndIdCompare = (a: Dashboard, b: Dashboard) =>
  a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
  int64.toNumber(b.id) - int64.toNumber(a.id);

export const creatorCompare = (a: Dashboard, b: Dashboard) =>
  a.metadata?.createdBy.toLowerCase().localeCompare(b.metadata?.createdBy.toLowerCase() || '') ||
  nameAndIdCompare(a, b);
